// src/SideWinder.Web/src/composables/useViewAs.ts
//
// Linked users (2026-09-28): the superuser's "view as" target — whose data
// the UI is showing while the superuser's own session cookie stays in place.
// Module-level state so every component shares one target; persisted in
// sessionStorage (per tab, gone when the tab closes) so a reload keeps it.
// Storage can throw (private mode, blocked site data), so every access is
// wrapped and the in-memory ref stays the source of truth.
//
// Task 7 uses it to route the Account panel's HL sign-in to the admin
// connect endpoints. Task 9 adds the X-SideWinder-View-As request header and
// the switcher UI on top of this same interface.
import { ref, type Ref } from 'vue'

export interface ViewAsTarget {
  userId: string
  label: string
}

const STORAGE_KEY = 'sidewinder:viewAs'

function load(): ViewAsTarget | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.userId === 'string' && typeof parsed.label === 'string') {
      return { userId: parsed.userId, label: parsed.label }
    }
  } catch {
    // unreadable or malformed: behave as if nothing is stored
  }
  return null
}

const target: Ref<ViewAsTarget | null> = ref(load())

function set(t: ViewAsTarget) {
  target.value = { userId: t.userId, label: t.label }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target.value))
  } catch {
    // storage unavailable: the in-memory target still applies for this page
  }
}

function clear() {
  target.value = null
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // storage unavailable: nothing to remove
  }
}

export function useViewAs() {
  return { target, set, clear }
}
